import React, { useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  Tooltip,
} from "@mui/material";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import InterviewState, {
  InterviewImportCapabilities,
} from "../../types/Interview";
import InterviewService, {
  InterviewOutcome,
} from "../../services/InterviewService";

export const importacaoPendente = (state: InterviewState | null) =>
  Boolean(state?.document_import && state.document_import.status !== "awaiting_clarification");

export const importacaoAguardaLeitura = (state: InterviewState | null) =>
  Boolean(
    state?.document_import &&
      ["processing", "awaiting_choice"].includes(state.document_import.status)
  );

export const validarArquivos = (
  files: File[],
  limits: InterviewImportCapabilities
): string | null => {
  if (files.length > limits.max_files)
    return `Selecione até ${limits.max_files} arquivos por importação.`;
  if (
    files.some(
      (file) =>
        !limits.extensions.includes(
          file.name.split(".").pop()?.toLowerCase() || ""
        )
    )
  )
    return "Formato não aceito. Use PDF, DOCX, XLSX ou CSV.";
  if (files.some((file) => file.size === 0))
    return "Não é possível importar um arquivo vazio.";
  if (files.some((file) => file.size > limits.max_file_bytes))
    return `Cada arquivo deve ter até ${limits.max_file_bytes / 1048576} MB.`;
  if (
    files.reduce((size, file) => size + file.size, 0) > limits.max_total_bytes
  )
    return `O conjunto deve ter até ${limits.max_total_bytes / 1048576} MB.`;
  return null;
};

/** Um único ciclo na página, compartilhado pelos dois locais do chat. */
export function useImportacaoDocumentos(
  state: InterviewState | null,
  receber: (outcome: InterviewOutcome) => void,
  busy: boolean
) {
  const [files, setFiles] = useState<File[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [acting, setActing] = useState(false);
  const stateRef = useRef(state);
  const receiveRef = useRef(receber);
  const busyRef = useRef(busy);
  const alive = useRef(true);
  const controller = useRef<AbortController | null>(null);
  const requestId = useRef<string | null>(null);
  stateRef.current = state;
  receiveRef.current = receber;
  busyRef.current = busy || uploading || acting;

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
      controller.current?.abort();
    };
  }, []);
  useEffect(() => {
    setFiles([]);
    setError(null);
    requestId.current = null;
    controller.current?.abort();
  }, [state?.interview_id]);

  const select = (next: File[]) => {
    const limits = stateRef.current?.import_capabilities;
    if (!limits) return;
    const message = validarArquivos(next, limits);
    setError(message);
    if (!message) {
      setFiles(next);
      requestId.current = null;
    }
  };

  const receiveAction = async (
    result: InterviewOutcome,
    requested: InterviewState
  ) => {
    if (!alive.current || stateRef.current?.interview_id !== requested.interview_id)
      return;
    if (stateRef.current === requested || result.kind === "failed") {
      receiveRef.current(result);
      return;
    }
    // Edições e turnos podem terminar enquanto o upload/ação está em andamento.
    // Releia o servidor para não substituir esse estado por uma resposta antiga.
    const before = stateRef.current;
    const fresh = await InterviewService.show(requested.interview_id);
    if (alive.current && stateRef.current === before) receiveRef.current(fresh);
  };

  const upload = async (message: string): Promise<boolean> => {
    const current = stateRef.current;
    if (!current?.import_capabilities || busyRef.current || !files.length)
      return false;
    const invalid = validarArquivos(files, current.import_capabilities);
    if (invalid) {
      setError(invalid);
      return false;
    }
    if (message.length > 4000) {
      setError("O pedido junto aos arquivos deve ter até 4000 caracteres.");
      return false;
    }
    requestId.current ??= window.crypto.randomUUID();
    const pendingRequestId = requestId.current;
    controller.current = new AbortController();
    busyRef.current = true;
    setUploading(true);
    setError(null);
    try {
      const result = await InterviewService.importDocuments(
        current.interview_id,
        files,
        current.revision,
        pendingRequestId,
        message,
        controller.current.signal
      );
      if (
        !alive.current ||
        stateRef.current?.interview_id !== current.interview_id
      )
        return false;
      await receiveAction(result, current);
      if (result.kind === "ok") {
        if (result.state.document_import_error) return false;
        setFiles([]);
        requestId.current = null;
        return true;
      }
      setError(result.msg);
      return false;
    } finally {
      if (alive.current) {
        busyRef.current = false;
        setUploading(false);
      }
    }
  };

  const choose = async (
    importId: string,
    candidateId: string
  ) => {
    const current = stateRef.current;
    if (!current || busyRef.current) return;
    busyRef.current = true;
    setActing(true);
    try {
      const result = await InterviewService.chooseImportScenario(
        current.interview_id,
        importId,
        current.revision,
        candidateId
      );
      await receiveAction(result, current);
    } finally {
      if (alive.current) {
        busyRef.current = false;
        setActing(false);
      }
    }
  };
  return { files, error, uploading, acting, select, upload, choose };
}

interface Props {
  state: InterviewState;
  control: ReturnType<typeof useImportacaoDocumentos>;
  disabled: boolean;
}

export function AnexarArquivos({ state, control, disabled }: Props) {
  const input = useRef<HTMLInputElement | null>(null);
  const limits = state.import_capabilities;
  if (!limits) return null;
  const locked = disabled || control.uploading || control.acting;
  return (
    <>
      <Tooltip title="Anexar arquivos (PDF, Word, Excel ou CSV).">
        <span>
          <IconButton aria-label="Anexar arquivos" disabled={locked} onClick={() => input.current?.click()}>
            <AttachFileIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
      <input
        ref={input}
        hidden
        type="file"
        multiple
        aria-label="Anexar arquivos ao Assistente IA"
        accept={limits.extensions.map((extension) => `.${extension}`).join(",")}
        disabled={locked}
        onChange={(event) => {
          control.select([...control.files, ...Array.from(event.target.files || [])]);
          event.target.value = "";
        }}
      />
    </>
  );
}

export default function ImportacaoDocumentos({
  state,
  control,
  disabled,
}: Props) {
  const limits = state.import_capabilities;
  if (!limits) return null;
  const locked = disabled || control.uploading || control.acting;
  const item = state.document_import;
  const choosing = item?.status === "awaiting_choice";
  if (control.uploading || (!control.files.length && !control.error && !choosing)) return null;
  return (
    <Box sx={{ px: 1.5, py: 0.5, maxHeight: 220, overflowY: "auto" }}>
      {control.files.map((file, index) => (
        <Chip
          key={`${file.name}:${index}`}
          size="small"
          sx={{ m: 0.25 }}
          label={file.name}
          disabled={locked}
          onDelete={() =>
            control.select(control.files.filter((_, i) => i !== index))
          }
        />
      ))}
      {control.error && <Alert severity="error">{control.error}</Alert>}
      {choosing && item?.candidates?.map((candidate) => (
        <Button
          key={candidate.id}
          size="small"
          disabled={locked}
          onClick={() => void control.choose(item.id, candidate.id)}
          title={candidate.description}
        >
          {candidate.title}
        </Button>
      ))}
    </Box>
  );
}
